// OpenEdX Environment Configuration
// This file replaces the virtual env.config module used by @edx/frontend-platform
// Webpack requires window.global to be defined, but vite doesn't do
// this for us.
window.global ||= window;

const isTruthyFlag = value => {
  if (!value) return false;
  return value.toLowerCase() === "true" || value === "1";
};

const isDemoMode =
  import.meta.env.MODE === "demo" &&
  isTruthyFlag(import.meta.env.VITE_LEARNING_DEMO_MODE);

const demoOrigin = window.location.origin;

const config = {
  // Basic configuration
  NODE_ENV:
    import.meta.env.VITE_NODE_ENV ||
    (import.meta.env.PROD ? "production" : "development"),
  PORT: parseInt(import.meta.env.VITE_PORT || "5007"),
  PUBLIC_PATH: "/learning/",
  APP_ID: "learning",

  // DATADOG
  DATADOG_CLIENT_TOKEN: import.meta.env.VITE_DATADOG_CLIENT_TOKEN || "",
  DATADOG_APPLICATION_ID: import.meta.env.VITE_DATADOG_APPLICATION_ID || "",
  DATADOG_API_KEY: import.meta.env.VITE_DATADOG_API_KEY || "",
  DATADOG_SESSION_SAMPLE_RATE:
    import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE || 100,
  DATADOG_SESSION_REPLAY_SAMPLE_RATE:
    import.meta.env.VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE || 100,
  PIPELINE_ENVIRONMENT: import.meta.env.VITE_PIPELINE_ENVIRONMENT || "",
  UI_VERSION: import.meta.env.VITE_UI_VERSION || "",

  // Authentication & Cookies
  ACCESS_TOKEN_COOKIE_NAME:
    import.meta.env.VITE_ACCESS_TOKEN_COOKIE_NAME ||
    "edx-jwt-cookie-header-payload",
  LANGUAGE_PREFERENCE_COOKIE_NAME:
    import.meta.env.VITE_LANGUAGE_PREFERENCE_COOKIE_NAME ||
    "openedx-language-preference",
  SESSION_COOKIE_DOMAIN:
    import.meta.env.VITE_SESSION_COOKIE_DOMAIN || "apps.local.openedx.io",
  USER_INFO_COOKIE_NAME:
    import.meta.env.VITE_USER_INFO_COOKIE_NAME || "edx-user-info",
  ACCESS_TOKEN_COOKIE_DOMAIN:
    import.meta.env.VITE_ACCESS_TOKEN_COOKIE_DOMAIN || ".local.openedx.io",

  // Base URLs
  BASE_URL: isDemoMode
    ? demoOrigin
    : import.meta.env.VITE_BASE_URL || "http://apps.local.openedx.io:5005",
  CREDENTIALS_BASE_URL:
    import.meta.env.VITE_CREDENTIALS_BASE_URL ||
    "http://apps.local.openedx.io:18150",
  ECOMMERCE_BASE_URL:
    import.meta.env.VITE_ECOMMERCE_BASE_URL ||
    "http://apps.local.openedx.io:18130",
  LMS_BASE_URL: isDemoMode
    ? demoOrigin
    : import.meta.env.VITE_LMS_BASE_URL || "http://local.openedx.io:8000",
  MARKETING_SITE_BASE_URL:
    import.meta.env.VITE_MARKETING_SITE_BASE_URL ||
    "http://local.openedx.io:8000",
  PORTAL_BASE_URL:
    import.meta.env.VITE_PORTAL_BASE_URL || "http://portal.local",

  // API Endpoints
  CSRF_TOKEN_API_PATH:
    import.meta.env.VITE_CSRF_TOKEN_API_PATH || "/csrf/api/v1/token",
  LOGIN_URL: isDemoMode
    ? `${demoOrigin}/login`
    : import.meta.env.VITE_LOGIN_URL ||
      "http://apps.local.openedx.io:5006/authn/login",
  LOGOUT_URL: isDemoMode
    ? `${demoOrigin}/logout`
    : import.meta.env.VITE_LOGOUT_URL || "http://local.openedx.io:8000/logout",
  ORDER_HISTORY_URL:
    import.meta.env.VITE_ORDER_HISTORY_URL ||
    "http://apps.local.openedx.io:1996/orders",
  REFRESH_ACCESS_TOKEN_ENDPOINT: isDemoMode
    ? `${demoOrigin}/login_refresh`
    : import.meta.env.VITE_REFRESH_ACCESS_TOKEN_ENDPOINT ||
      "http://local.openedx.io:8000/login_refresh",
  // In demo mode, set to empty string so the platform skips config fetch entirely.
  // The Vite dev-server plugin used to serve this, but on static S3 hosting
  // there is no server — an empty value avoids a blocking fetch failure.
  MFE_CONFIG_API_URL: isDemoMode
    ? ""
    : import.meta.env.VITE_MFE_CONFIG_API_URL ||
      "http://local.openedx.io:8000/api/mfe_config/v1",

  // Branding & Assets
  LOGO_URL:
    import.meta.env.VITE_LOGO_URL || "https://edx-cdn.org/v3/default/logo.svg",
  LOGO_TRADEMARK_URL:
    import.meta.env.VITE_LOGO_TRADEMARK_URL ||
    "https://edx-cdn.org/v3/default/logo-trademark.svg",
  LOGO_WHITE_URL:
    import.meta.env.VITE_LOGO_WHITE_URL ||
    "https://edx-cdn.org/v3/default/logo-white.svg",
  FAVICON_URL:
    import.meta.env.VITE_FAVICON_URL ||
    "https://edx-cdn.org/v3/default/favicon.ico",
  BANNER_IMAGE_LARGE: import.meta.env.VITE_BANNER_IMAGE_LARGE || "",
  BANNER_IMAGE_MEDIUM: import.meta.env.VITE_BANNER_IMAGE_MEDIUM || "",
  BANNER_IMAGE_SMALL: import.meta.env.VITE_BANNER_IMAGE_SMALL || "",
  BANNER_IMAGE_EXTRA_SMALL: import.meta.env.VITE_BANNER_IMAGE_EXTRA_SMALL || "",
  ASSET_URL: import.meta.env.VITE_ASSET_URL || "",

  // Site Information
  SITE_NAME: import.meta.env.VITE_SITE_NAME || "OEX Learning App",
  INFO_EMAIL: import.meta.env.VITE_INFO_EMAIL || "admin@euka.edu.au",

  // Features
  ENABLE_DYNAMIC_REGISTRATION_FIELDS:
    import.meta.env.VITE_ENABLE_DYNAMIC_REGISTRATION_FIELDS || "true",
  ENABLE_PROGRESSIVE_PROFILING_ON_AUTHN:
    import.meta.env.VITE_ENABLE_PROGRESSIVE_PROFILING_ON_AUTHN || "true",
  SHOW_REGISTRATION_LINKS: true,
  ENABLE_IMAGE_LAYOUT: false,

  // Support Links
  LOGIN_ISSUE_SUPPORT_LINK:
    import.meta.env.VITE_LOGIN_ISSUE_SUPPORT_LINK ||
    "http://local.openedx.io:8000/login-issue-support-url",
  TOS_AND_HONOR_CODE:
    import.meta.env.VITE_TOS_AND_HONOR_CODE ||
    "http://local.openedx.io:8000/honor",
  TOS_LINK: import.meta.env.VITE_TOS_LINK || "http://local.openedx.io:8000/tos",
  PRIVACY_POLICY:
    import.meta.env.VITE_PRIVACY_POLICY ||
    "http://local.openedx.io:8000/privacy",
  AUTHN_PROGRESSIVE_PROFILING_SUPPORT_LINK:
    import.meta.env.VITE_AUTHN_PROGRESSIVE_PROFILING_SUPPORT_LINK ||
    "http://apps.local.openedx.io:1999/welcome",

  // Analytics & Tracking
  SEGMENT_KEY: import.meta.env.VITE_SEGMENT_KEY || "",

  // Miscellaneous
  ZENDESK_KEY: import.meta.env.VITE_ZENDESK_KEY || "",
  ZENDESK_LOGO_URL: import.meta.env.VITE_ZENDESK_LOGO_URL || "",

  // Usersnap Configuration
  USERSNAP_API_KEY: import.meta.env.VITE_USERSNAP_API_KEY || "",
  USERSNAP_REPORT_ISSUE_WIDGET_ID:
    import.meta.env.VITE_USERSNAP_REPORT_ISSUE_WIDGET_ID || "",
  USERSNAP_SUGGEST_FEEDBACK_WIDGET_ID:
    import.meta.env.VITE_USERSNAP_SUGGEST_FEEDBACK_WIDGET_ID || "",
  // BLD2-461: widget ID for the "User Suggested Feedback" form opened from
  // the Read Aloud notification panel. Confirm UUID with Usersnap integration
  // owner before adding to .env.rc / .env.prod.
  USERSNAP_READ_ALOUD_FEEDBACK_WIDGET_ID:
    import.meta.env.VITE_USERSNAP_READ_ALOUD_FEEDBACK_WIDGET_ID || "",

  // Heap analytics id
  HEAP_ANALYTICS_ID: import.meta.env.VITE_HEAP_ANALYTICS_ID || ""
};

export default config;
